// src/components/Booking/BookingModal.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FiX,
  FiCalendar,
  FiClock,
  FiMapPin,
  FiUser,
  FiMail,
  FiPhone,
  FiHome,
  FiCheck,
  FiSave,
  FiChevronDown,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { useTenant } from "../../context/TenantContext";
import api from "../../services/api";
import toast from "react-hot-toast";
import BookingCalendarPopup, { parseKey } from "./BookingCalendarPopup";
import "../../styles/booking-modal.css";
import "../../styles/booking-calendar.css";

const southAfricanProvinces = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "North West",
  "Northern Cape",
  "Western Cape",
];

const countries = [
  "South Africa",
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Nigeria",
  "Kenya",
  "Egypt",
  "Other",
];

// The API may return "2026-10-21" or a full ISO string; the calendar works with "YYYY-MM-DD"
const dateKey = (value) => String(value).slice(0, 10);

// Profiles come back as PascalCase or camelCase depending on the source
const readProfile = (p = {}) => ({
  customerName: p.UserName || p.name || p.userName || "",
  customerEmail: p.Email || p.email || "",
  customerPhone: p.Phone || p.phone || "",
  addressLine1: p.AddressLine1 || p.addressLine1 || "",
  addressLine2: p.AddressLine2 || p.addressLine2 || "",
  city: p.City || p.city || "",
  state: p.State || p.state || "",
  postalCode: p.PostalCode || p.postalCode || "",
  country: p.Country || p.country || "South Africa",
});

const BookingModal = ({ isOpen, onClose, product, onSuccess }) => {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [dateLoadError, setDateLoadError] = useState("");
  const [slotsLoadError, setSlotsLoadError] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [useSavedAddress, setUseSavedAddress] = useState(true);
  const [saveNewAddress, setSaveNewAddress] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [hasSavedAddress, setHasSavedAddress] = useState(false);

  const [formData, setFormData] = useState({
    bookingDate: "",
    startTime: "",
    endTime: "",
    notes: "",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "South Africa",
  });

  // "YYYY-MM-DD" -> the raw value the API gave us (sent back unchanged)
  const rawByKey = useMemo(() => {
    const map = {};
    availableDates.forEach((d) => {
      map[dateKey(d)] = d;
    });
    return map;
  }, [availableDates]);
  const availableKeys = useMemo(() => Object.keys(rawByKey), [rawByKey]);
  const selectedKey = formData.bookingDate ? dateKey(formData.bookingDate) : "";

  const applyProfile = useCallback((profile) => {
    const p = readProfile(profile);
    setFormData((prev) => ({ ...prev, ...p }));
    const hasAddress = !!(p.addressLine1 && p.city && p.postalCode);
    setHasSavedAddress(hasAddress);
    setUseSavedAddress(hasAddress);
  }, []);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await api.get("/api/auth/me");
      if (response.data) {
        setUserProfile(response.data);
        applyProfile(response.data);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      if (user) applyProfile(user); // fall back to the auth context
    }
  }, [user, applyProfile]);

  const fetchAvailableDates = useCallback(async () => {
    if (!product?.ProductID) return;
    setLoadingDates(true);
    try {
      const response = await api.get(
        `/api/bookings/available-dates/${product.ProductID}`,
      );
      if (!response.data.success) {
        throw new Error(response.data.message || "Available dates could not be loaded");
      }
      setDateLoadError("");
      setAvailableDates(response.data.dates || []);
    } catch (error) {
      console.error("Error fetching available dates:", error);
      setAvailableDates([]);
      setDateLoadError("Could not load available dates. Please try again.");
    } finally {
      setLoadingDates(false);
    }
  }, [product?.ProductID]);

  const fetchAvailableSlots = useCallback(async () => {
    if (!formData.bookingDate || !product?.ProductID) return;
    setLoadingSlots(true);
    try {
      const response = await api.get(
        `/api/bookings/availability/${product.ProductID}`,
        { params: { date: dateKey(formData.bookingDate) } },
      );
      if (!response.data.success) {
        throw new Error(response.data.message || "Available times could not be loaded");
      }
      setSlotsLoadError("");
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error("Error fetching slots:", error);
      setAvailableSlots([]);
      setSlotsLoadError("Could not load available times. Please try again.");
    } finally {
      setLoadingSlots(false);
    }
  }, [formData.bookingDate, product?.ProductID]);

  // Load everything when the modal opens
  useEffect(() => {
    if (isOpen && product?.ProductID) {
      fetchAvailableDates();
      fetchUserProfile();
      setFormData((prev) => ({ ...prev, bookingDate: "", startTime: "", endTime: "" }));
      setAvailableSlots([]);
      setAvailableDates([]);
      setDateLoadError("");
      setSlotsLoadError("");
      setSaveNewAddress(false);
      setShowCalendar(false);
    }
  }, [isOpen, product?.ProductID, fetchAvailableDates, fetchUserProfile]);

  // Reload slots whenever the date changes
  useEffect(() => {
    if (formData.bookingDate) fetchAvailableSlots();
    else setAvailableSlots([]);
  }, [formData.bookingDate, fetchAvailableSlots]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Fallback: derive end time from the service duration
    if (name === "startTime" && value && product?.Duration) {
      const [hour, minute] = value.split(":");
      const endMinutes =
        parseInt(hour) * 60 + parseInt(minute) + parseInt(product.Duration);
      const endTime = `${String(Math.floor(endMinutes / 60)).padStart(2, "0")}:${String(endMinutes % 60).padStart(2, "0")}`;
      setFormData((prev) => ({ ...prev, endTime }));
    }
  };

  const handleSelectDate = (key) => {
    // New date means the previously chosen time no longer applies
    setFormData((prev) => ({
      ...prev,
      bookingDate: rawByKey[key] || key,
      startTime: "",
      endTime: "",
    }));
    setShowCalendar(false);
  };

  const handleSelectSlot = (slot) => {
    if (slot.available <= 0) return;
    handleChange({ target: { name: "startTime", value: slot.start } });
    if (slot.end) setFormData((prev) => ({ ...prev, endTime: slot.end }));
  };

  const validateForm = () => {
    const f = formData;
    const checks = [
      [!f.bookingDate, "Please select a date"],
      [!f.startTime, "Please select a time slot"],
      [!f.endTime, "Please select an end time"],
      [!f.customerName?.trim(), "Please enter your name"],
      [!f.customerEmail?.trim(), "Please enter your email"],
      [
        f.customerEmail?.trim() && !/^\S+@\S+\.\S+$/.test(f.customerEmail),
        "Please enter a valid email address",
      ],
      [!f.customerPhone?.trim(), "Please enter your phone number"],
      [!f.addressLine1?.trim(), "Please enter your street address"],
      [!f.city?.trim(), "Please enter your city"],
      [!f.postalCode?.trim(), "Please enter your postal code"],
    ];
    const failed = checks.find(([bad]) => bad);
    if (failed) {
      toast.error(failed[1]);
      return false;
    }
    return true;
  };

  const handleUseSavedAddress = () => {
    setUseSavedAddress(true);
    if (userProfile) {
      const { addressLine1, addressLine2, city, state, postalCode, country } =
        readProfile(userProfile);
      setFormData((prev) => ({
        ...prev,
        addressLine1,
        addressLine2,
        city,
        state,
        postalCode,
        country,
      }));
    }
  };

  const handleEnterNewAddress = () => {
    setUseSavedAddress(false);
    setSaveNewAddress(true);
    setFormData((prev) => ({
      ...prev,
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "South Africa",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (saveNewAddress && !hasSavedAddress) {
        try {
          await api.put("/api/auth/profile", {
            addressLine1: formData.addressLine1,
            addressLine2: formData.addressLine2,
            city: formData.city,
            state: formData.state,
            postalCode: formData.postalCode,
            country: formData.country,
          });
          toast.success("Address saved to your profile");
        } catch (err) {
          console.error("Error saving address:", err);
        }
      }

      const response = await api.post("/api/bookings", {
        ProductID: product.ProductID,
        BookingDate: formData.bookingDate,
        StartTime: formData.startTime,
        EndTime: formData.endTime,
        CustomerName: formData.customerName,
        CustomerEmail: formData.customerEmail,
        CustomerPhone: formData.customerPhone,
        Notes: formData.notes,
      });

      if (response.data.success) {
        toast.success(
          "Booking created and added to cart! Redirecting to checkout...",
          { duration: 3000 },
        );
        onSuccess && onSuccess();
        onClose();
        setTimeout(() => {
          window.location.href = "/checkout";
        }, 1500);
      }
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(error.response?.data?.message || "Failed to create booking");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formatDate = (value) =>
    parseKey(dateKey(value)).toLocaleDateString("en-ZA", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  const locationText =
    product?.LocationType === "online"
      ? "Online / Virtual Meeting"
      : "We will come to your address";

  const accent = tenant?.PrimaryColor;

  return (
    <div className="booking-modal-overlay" onClick={onClose}>
      <div
        className="booking-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={accent ? { "--bc-accent": accent } : undefined}
      >
        <div className="booking-modal-header">
          <h2>Book {product?.Name}</h2>
          <button
            type="button"
            className="booking-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="booking-modal-form">
          {/* Service Info */}
          <div className="booking-service-info">
            <div className="service-price">
              R{product?.SellingPrice?.toLocaleString()}
            </div>
            <div className="service-duration">
              <FiClock /> Duration: {product?.Duration} minutes
            </div>
            <div className="service-location">
              <FiMapPin /> {locationText}
            </div>
          </div>

          {/* Date: opens the calendar popup */}
          <div className="booking-form-group">
            <label>
              <FiCalendar /> Select Date *
            </label>
            {loadingDates ? (
              <div className="booking-slots-loading">
                Loading available dates...
              </div>
            ) : dateLoadError ? (
              <div className="booking-no-slots" role="alert">
                {dateLoadError}
              </div>
            ) : availableKeys.length > 0 ? (
              <button
                type="button"
                className={`bc-date-trigger ${selectedKey ? "has-value" : ""}`}
                onClick={() => setShowCalendar(true)}
                aria-haspopup="dialog"
              >
                <FiCalendar />
                <span>
                  {formData.bookingDate
                    ? formatDate(formData.bookingDate)
                    : "Choose a date"}
                </span>
                <FiChevronDown />
              </button>
            ) : (
              <div className="booking-no-slots">
                No available dates for this service. Please check back later.
              </div>
            )}
          </div>

          {/* Time slots */}
          {formData.bookingDate && (
            <div className="booking-form-group">
              <label>
                <FiClock /> Select Time Slot *
              </label>
              {loadingSlots ? (
                <div className="booking-slots-loading">
                  Loading available slots...
                </div>
              ) : slotsLoadError ? (
                <div className="booking-no-slots" role="alert">
                  {slotsLoadError}
                </div>
              ) : availableSlots.length > 0 ? (
                <div className="booking-time-slots">
                  {availableSlots.map((slot, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`booking-time-slot ${formData.startTime === slot.start ? "selected" : ""} ${slot.available === 0 ? "booked" : ""}`}
                      onClick={() => handleSelectSlot(slot)}
                      disabled={slot.available === 0}
                    >
                      <span className="slot-time">
                        {slot.start} - {slot.end}
                      </span>
                      {slot.available > 0 ? (
                        <span className="slot-available">
                          {slot.available} spot{slot.available !== 1 ? "s" : ""}{" "}
                          left
                        </span>
                      ) : (
                        <span className="slot-booked">Fully Booked</span>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="booking-no-slots">
                  No available time slots for this date. Please select another
                  date.
                </div>
              )}
            </div>
          )}

          {/* Customer Information */}
          <div className="booking-form-section">
            <h3>
              <FiUser /> Your Information
            </h3>

            <div className="booking-form-group">
              <label>
                <FiUser /> Full Name *
              </label>
              <input
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
                required
                placeholder="Enter your full name"
              />
            </div>

            <div className="booking-form-group">
              <label>
                <FiMail /> Email Address *
              </label>
              <input
                type="email"
                name="customerEmail"
                value={formData.customerEmail}
                onChange={handleChange}
                required
                placeholder="Enter your email"
              />
            </div>

            <div className="booking-form-group">
              <label>
                <FiPhone /> Phone Number *
              </label>
              <input
                type="tel"
                name="customerPhone"
                value={formData.customerPhone}
                onChange={handleChange}
                required
                placeholder="Enter your phone number"
              />
            </div>
          </div>

          {/* Service Address */}
          <div className="booking-form-section">
            <h3>
              <FiHome /> Service Address
            </h3>
            <p className="address-note">
              {product?.LocationType === "online"
                ? "This is an online service. The address below will be used for correspondence."
                : "Where should we provide the service?"}
            </p>

            {hasSavedAddress && (
              <div className="booking-address-toggle">
                <label className={useSavedAddress ? "active" : ""}>
                  <input
                    type="radio"
                    checked={useSavedAddress}
                    onChange={handleUseSavedAddress}
                  />
                  <FiHome /> Use saved address
                </label>
                <label className={!useSavedAddress ? "active" : ""}>
                  <input
                    type="radio"
                    checked={!useSavedAddress}
                    onChange={handleEnterNewAddress}
                  />
                  <FiCheck /> Enter new address
                </label>
              </div>
            )}

            {useSavedAddress && hasSavedAddress && (
              <div className="booking-saved-address-display">
                <div className="saved-address-card">
                  <div className="saved-address-details">
                    <p>
                      <strong>Saved Address:</strong>
                    </p>
                    <p>{formData.addressLine1}</p>
                    {formData.addressLine2 && <p>{formData.addressLine2}</p>}
                    <p>
                      {formData.city}, {formData.postalCode}
                    </p>
                    <p>
                      {formData.state} {formData.country}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {(!useSavedAddress || !hasSavedAddress) && (
              <div className="booking-new-address">
                <div className="booking-form-group">
                  <label>Street Address *</label>
                  <input
                    type="text"
                    name="addressLine1"
                    value={formData.addressLine1}
                    onChange={handleChange}
                    required
                    placeholder="House number and street name"
                  />
                </div>
                <div className="booking-form-group">
                  <label>Address Line 2 (Optional)</label>
                  <input
                    type="text"
                    name="addressLine2"
                    value={formData.addressLine2}
                    onChange={handleChange}
                    placeholder="Apartment, suite, unit, etc."
                  />
                </div>
                <div className="booking-form-row">
                  <div className="booking-form-group half">
                    <label>City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      placeholder="City"
                    />
                  </div>
                  <div className="booking-form-group half">
                    <label>Postal Code *</label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleChange}
                      required
                      placeholder="Postal code"
                    />
                  </div>
                </div>
                <div className="booking-form-row">
                  <div className="booking-form-group half">
                    <label>State/Province</label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="booking-select-province"
                    >
                      <option value="">Select Province</option>
                      {southAfricanProvinces.map((province) => (
                        <option key={province} value={province}>
                          {province}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="booking-form-group half">
                    <label>Country</label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="booking-select-country"
                    >
                      {countries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {!hasSavedAddress && (
                  <div className="booking-save-address-option">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={saveNewAddress}
                        onChange={(e) => setSaveNewAddress(e.target.checked)}
                      />
                      <span>
                        <FiSave /> Save this address to my profile for future
                        bookings
                      </span>
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="booking-form-group">
            <label>Special Requests / Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Any special requests or additional information..."
            />
          </div>

          {/* Actions */}
          <div className="booking-modal-actions">
            <button
              type="button"
              className="booking-btn-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="booking-btn-submit"
              disabled={loading}
            >
              {loading ? "Processing..." : "Confirm Booking"}
            </button>
          </div>
        </form>
      </div>

      {showCalendar && (
        <BookingCalendarPopup
          availableKeys={availableKeys}
          selectedKey={selectedKey}
          accentColor={accent}
          onSelect={handleSelectDate}
          onClose={() => setShowCalendar(false)}
        />
      )}
    </div>
  );
};

export default BookingModal;
